import {matchRoutes, useLocation} from 'react-router-dom';
import type {RouteObject} from 'react-router-dom';
import pages, {Page} from '../pages';

const toRouteObjects = (list: Page[]): RouteObject[] =>
    list.map(page => ({
        path: page.path,
        handle: page,
        children: page.children ? toRouteObjects(page.children) : undefined
    }));

const routeObjects = toRouteObjects(pages);

/**
 * Returns the declarative Page entry (from pages.ts) matching the current
 * location, so layout chrome can react to page-level flags like fullBleed.
 */
export function usePageConfig(): Page | undefined {
    const location = useLocation();
    const matches = matchRoutes(routeObjects, location);
    if (!matches || matches.length === 0) {
        return undefined;
    }
    return matches[matches.length - 1].route.handle as Page;
}

/**
 * The matched route as a pattern with its `:param` tokens (e.g.
 * "/projects/:projectId"), joined across nested pages, plus the leaf page's
 * title. Undefined when nothing matches.
 */
export function useRouteMatch(): {pattern: string; title?: string} | undefined {
    const location = useLocation();
    const matches = matchRoutes(routeObjects, location);
    if (!matches || matches.length === 0) {
        return undefined;
    }
    const pattern =
        '/' +
        matches
            .map(match => match.route.path ?? '')
            .filter(Boolean)
            .join('/')
            .replace(/\/+/g, '/')
            .replace(/^\//, '');
    const leaf = matches[matches.length - 1].route.handle as Page;
    return {pattern, title: leaf.title};
}
